import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Navigate } from 'react-router';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheck } from '#src/atoms/icons/system/iconCheck.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { BodyM, HeadlineM, HeadlineS } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { AuthenticityToken } from '#src/components/form/AuthenticityToken/index.tsx';
import { API_ROOT } from '#src/config.ts';
import { LANDING } from '#src/constants/__generated__/routes.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useQueryString } from '#src/hooks/useQueryString.ts';

import type {
  OAuthApplicationQuery,
  OAuthApplicationQueryVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Authorization required',
  },
  subtitle: {
    defaultMessage: '<b>{name}</b> wants to access your Sorare account',
  },
  scopes: {
    defaultMessage: 'This will allow {name} to:',
  },
  nickname: {
    defaultMessage: 'Access your username, your avatar and your wallet address',
  },
  cards: {
    defaultMessage:
      'Access details about your Cards, your achievements and your favorites',
  },
  auctions: {
    defaultMessage:
      'Access details about your auctions, your offers and your notifications',
  },
  cannot: {
    defaultMessage: 'This will NOT allow {name} to:',
  },
  email: {
    defaultMessage: 'Access your email address',
  },
  cannot_so5: {
    defaultMessage:
      'List your future lineups, compose your lineups, claim your rewards',
  },
  cannot_market: {
    defaultMessage:
      'Bid on Cards, sell your Cards, make offers, accept offers or initiate a withdrawal',
  },
});

const Body = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 var(--triple-unit) var(--triple-unit);
`;
const Footer = styled(Horizontal)`
  form {
    margin-bottom: 0;
    width: 100%;
  }
`;

const Logo = styled.img`
  width: 64px;
`;
const Icon = styled(FontAwesomeIcon)`
  margin-right: var(--unit);
`;

const authorizeEndpoint = `${API_ROOT}/oauth/authorize`;
const OAUTH_APPLICATION_QUERY = gql`
  query OAuthApplicationQuery($clientId: String!) {
    oauthApplication(clientId: $clientId) {
      id
      name
      pictureUrl
      scopes
      myAccessToken {
        id
      }
    }
  }
` as TypedDocumentNode<OAuthApplicationQuery, OAuthApplicationQueryVariables>;

export const OAuth = () => {
  const clientId = useQueryString('client_id');
  const state = useQueryString('state');
  const { data, loading } = useQuery(OAUTH_APPLICATION_QUERY, {
    variables: {
      clientId: clientId!,
    },
    skip: !clientId,
  });
  const redirectUri = useQueryString('redirect_uri');

  const authorizeFormIsMounted = useCallback((node: HTMLFormElement | null) => {
    node?.submit();
  }, []);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data?.oauthApplication) return <Navigate replace to={LANDING} />;

  const { name, pictureUrl, scopes, myAccessToken } = data.oauthApplication!;

  const renderForm = () => {
    return (
      <>
        <AuthenticityToken />
        <input type="hidden" name="client_id" value={clientId} />
        <input type="hidden" name="redirect_uri" value={redirectUri} />
        <input type="hidden" name="state" value={state} />
        <input type="hidden" name="response_type" value="code" />
        <input type="hidden" name="scope" value={scopes || 'public'} />
        <input type="hidden" name="code_challenge" />
        <input type="hidden" name="code_challenge_method" />
      </>
    );
  };

  const renderAllowedMessage = (message: MessageDescriptor) => {
    return (
      <Horizontal gap={0.5} key={message.id}>
        <Icon icon={iconCheck} />
        <BodyM>
          <FormattedMessage {...message} />
        </BodyM>
      </Horizontal>
    );
  };

  const renderBlockedMessage = (message: MessageDescriptor) => {
    return (
      <Horizontal gap={0.5} key={message.id}>
        <Icon icon={iconX} />
        <BodyM>
          <FormattedMessage {...message} />
        </BodyM>
      </Horizontal>
    );
  };

  if (myAccessToken) {
    return (
      <>
        <LoadingIndicator fullHeight />
        <form
          action={authorizeEndpoint}
          method="post"
          ref={authorizeFormIsMounted}
        >
          {renderForm()}
        </form>
      </>
    );
  }

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open
      title={
        <HeadlineM as="h3" className="text-center">
          <FormattedMessage {...messages.title} />
        </HeadlineM>
      }
      footer={
        <Footer>
          <form action={authorizeEndpoint} method="post">
            {renderForm()}
            <Button color="primary" size="medium" type="submit" fullWidth>
              <FormattedMessage defaultMessage="Authorize" />
            </Button>
          </form>
          <form action={authorizeEndpoint} method="post">
            <input type="hidden" name="_method" value="delete" />
            {renderForm()}
            <Button size="medium" color="destructive" type="submit" fullWidth>
              <FormattedMessage defaultMessage="Deny" />
            </Button>
          </form>
        </Footer>
      }
    >
      <Body>
        {!!pictureUrl && (
          <Horizontal gap={0} center>
            <Logo alt={name} src={pictureUrl} />
          </Horizontal>
        )}
        <HeadlineS as="h5">
          <FormattedMessage {...messages.subtitle} values={{ name, b: Bold }} />
        </HeadlineS>
        <Vertical>
          <BodyM bold>
            <FormattedMessage {...messages.scopes} values={{ name }} />
          </BodyM>

          {[
            scopes?.includes('email') && messages.email,
            messages.nickname,
            messages.cards,
            messages.auctions,
          ].map(m => m && renderAllowedMessage(m))}
        </Vertical>
        <Vertical>
          <BodyM bold>
            <FormattedMessage {...messages.cannot} values={{ name }} />
          </BodyM>
          {[
            !scopes?.includes('email') && messages.email,
            messages.cannot_so5,
            messages.cannot_market,
          ].map(m => m && renderBlockedMessage(m))}
        </Vertical>
      </Body>
    </Dialog>
  );
};
